<template>
  <div class="notes" :class="{ dense }">
    <section v-for="group in groups" :key="group.date" class="day">
      <h2 class="date">{{ formatChangelogDate(group.date, locale) }}</h2>
      <article
        v-for="release in group.releases"
        :key="release.id"
        class="release"
      >
        <h3>{{ release.title[locale] }}</h3>
        <ul>
          <li
            v-for="(item, index) in release.items[locale]"
            :key="`${release.id}-${index}`"
          >
            <p>
              <template
                v-for="part in keyedParts(item)"
                :key="part.n"
              >
                <strong v-if="part.bold">{{ part.text }}</strong>
                <template v-else>{{ part.text }}</template>
              </template>
            </p>
            <figure
              v-for="fig in figuresFor(release, index)"
              :key="`${fig.src}-${index}`"
            >
              <img :src="fig.src" :alt="fig.alt[locale]" loading="lazy" />
            </figure>
          </li>
        </ul>
      </article>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  changelogSegments,
  formatChangelogDate,
  type ChangelogRelease,
} from '../i18n/changelog'
import { useI18n } from '../i18n/t'

const props = withDefaults(
  defineProps<{
    releases: ChangelogRelease[]
    /** Screenshots under the matching bullet. Off in the desktop dialog. */
    showFigures?: boolean
    dense?: boolean
  }>(),
  { showFigures: false, dense: false },
)

const { locale } = useI18n()

const groups = computed(() => {
  const out: { date: string; releases: ChangelogRelease[] }[] = []
  for (const release of props.releases) {
    const last = out[out.length - 1]
    if (last && last.date === release.date) last.releases.push(release)
    else out.push({ date: release.date, releases: [release] })
  }
  return out
})

function figuresFor(release: ChangelogRelease, item: number) {
  if (!props.showFigures) return []
  return (release.figures ?? []).filter((fig) => fig.item === item)
}

function keyedParts(item: string) {
  return changelogSegments(item).map((part, n) => ({ ...part, n }))
}
</script>

<style scoped>
.day + .day {
  margin-top: 28px;
}

.date {
  margin: 0 0 12px;
  color: var(--md-sys-color-primary);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.release + .release {
  margin-top: 18px;
}

h3 {
  margin: 0 0 8px;
  color: var(--md-sys-color-on-surface);
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
}

ul {
  margin: 0;
  padding: 0 0 0 1.15em;
}

li {
  margin: 0 0 8px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

li p {
  margin: 0;
}

strong {
  color: var(--md-sys-color-on-surface);
  font-weight: 700;
}

figure {
  margin: 10px 0 4px;
}

img {
  display: block;
  width: min(100%, 420px);
  height: auto;
  border-radius: 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container);
}

.dense .day + .day {
  margin-top: 20px;
}

.dense h3 {
  font-size: 16px;
  line-height: 22px;
}

.dense li {
  margin-bottom: 6px;
}
</style>
